import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import * as asuntosApi from '@/api/asuntos.api';
import * as frentesApi from '@/api/frentes.api';

/**
 * PONER ALGO A MANO.
 *
 * Esteban, 20-sep: *"tener la capacidad de yo mismo crear y utilizar esto como
 * mi agenda personal, mi agenda de todas mis cosas"*.
 *
 * Hasta hoy todo asunto lo detectaba la IA leyendo un chat. Eso hacía que la
 * aplicación fuera un lector de WhatsApp y no una agenda: lo que no pasó por un
 * mensaje no podía existir, y media vida de una persona no pasa por WhatsApp.
 *
 * ═══ CUATRO CAMPOS, Y LOS DEMÁS ESCONDIDOS ═══
 *
 * Qué, cuándo, en qué frente, y cuánto urge. Nada más a la vista. Un
 * formulario de nueve campos para anotar "llamar al dentista" es un formulario
 * que no se usa, y una agenda que cuesta llenar se llena en otro lado.
 *
 * El monto está detrás de un botón: la mayoría de las cosas no son de plata.
 */

const RAPIDAS = [
  { et: 'Hoy', h: 3 },
  { et: 'Mañana', h: 24 },
  { et: 'En 3 días', h: 72 },
  { et: 'En una semana', h: 168 },
];

/** `datetime-local` quiere `YYYY-MM-DDTHH:mm` en hora LOCAL, no ISO en UTC. */
function paraElCampo(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  /** Frente preseleccionado, si se abre desde uno. */
  frenteId?: string;
}

export function NuevoAsunto({ abierto, onCerrar, frenteId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [que, setQue] = useState('');
  const [cuando, setCuando] = useState(() => paraElCampo(new Date(Date.now() + 3 * 3_600_000)));
  const [frente, setFrente] = useState<string | undefined>(frenteId);
  const [prioridad, setPrioridad] = useState<'ALTA' | 'MEDIA' | 'BAJA'>('MEDIA');
  const [verMonto, setVerMonto] = useState(false);
  const [monto, setMonto] = useState('');
  const [moneda, setMoneda] = useState<'DOP' | 'USD'>('DOP');

  const frentes = useQuery({
    queryKey: ['frentes'],
    queryFn: frentesApi.getFrentes,
    enabled: abierto,
  });

  const crear = useMutation({
    mutationFn: () =>
      asuntosApi.crearAsunto({
        descripcion: que.trim(),
        // El campo da hora local; `new Date` la interpreta como tal y el ISO
        // sale con el desfase correcto. Mandar el texto crudo lo guardaría
        // como UTC y todo aparecería cuatro horas corrido.
        fechaCompromiso: new Date(cuando).toISOString(),
        frenteId: frente ?? null,
        prioridad,
        montoCentavos: verMonto && monto ? Math.round(Number(monto) * 100) : null,
        moneda: verMonto && monto ? moneda : null,
      }),
    onSuccess: () => {
      toast.success('Anotado');
      void qc.invalidateQueries({ queryKey: ['asuntos'] });
      void qc.invalidateQueries({ queryKey: ['frentes'] });
      setQue('');
      setMonto('');
      setVerMonto(false);
      onCerrar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!abierto) return null;

  const puede = que.trim().length > 0 && cuando.length > 0;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px]"
        onClick={onCerrar}
        aria-hidden
      />
      <div className="fixed inset-x-0 bottom-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-2xl bg-white p-4 shadow-2xl sm:inset-x-auto sm:right-6 sm:top-20 sm:w-[420px] sm:rounded-2xl dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50">
            Anotar algo
          </h2>
          <button
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-11 w-11 place-items-center rounded-full text-slate-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <input
          autoFocus
          value={que}
          onChange={(e) => setQue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && puede && crear.mutate()}
          placeholder="Llamar al dentista"
          className="min-h-[48px] w-full rounded-lg border border-slate-200 px-3 text-base dark:border-slate-700 dark:bg-slate-900"
        />

        <div className="mt-3">
          <p className="mb-1.5 text-xs font-medium text-slate-500">¿Cuándo?</p>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {RAPIDAS.map((r) => (
              <button
                key={r.et}
                onClick={() => setCuando(paraElCampo(new Date(Date.now() + r.h * 3_600_000)))}
                className="min-h-[36px] rounded-full bg-slate-100 px-3 text-xs dark:bg-slate-800"
              >
                {r.et}
              </button>
            ))}
          </div>
          <input
            type="datetime-local"
            value={cuando}
            onChange={(e) => setCuando(e.target.value)}
            className="min-h-[44px] w-full rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
          />
        </div>

        <div className="mt-3">
          <p className="mb-1.5 text-xs font-medium text-slate-500">¿En qué frente?</p>
          <div className="flex flex-wrap gap-1.5">
            {(frentes.data?.frentes ?? []).map((f) => (
              <button
                key={f.id}
                onClick={() => setFrente(frente === f.id ? undefined : f.id)}
                className={cn(
                  'flex min-h-[36px] items-center gap-1.5 rounded-full border px-3 text-xs',
                  frente === f.id
                    ? 'border-transparent text-white'
                    : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300',
                )}
                style={frente === f.id ? { backgroundColor: f.color } : undefined}
              >
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: frente === f.id ? '#fff' : f.color }}
                />
                {f.nombre}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-3">
          <p className="mb-1.5 text-xs font-medium text-slate-500">¿Cuánto urge?</p>
          <div className="flex gap-1.5">
            {(['ALTA', 'MEDIA', 'BAJA'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPrioridad(p)}
                className={cn(
                  'min-h-[36px] flex-1 rounded-lg text-xs',
                  prioridad === p
                    ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
                )}
              >
                {p === 'ALTA' ? 'Urgente' : p === 'MEDIA' ? 'Importante' : 'Puede esperar'}
              </button>
            ))}
          </div>
        </div>

        {/* El monto, escondido: la mayoría de las cosas no son de plata, y un
            campo vacío en cada anotación es un campo que estorba. */}
        {verMonto ? (
          <div className="mt-3 flex gap-2">
            <input
              type="number"
              inputMode="decimal"
              value={monto}
              onChange={(e) => setMonto(e.target.value)}
              placeholder="0.00"
              className="min-h-[44px] flex-1 rounded-lg border border-slate-200 px-3 text-sm dark:border-slate-700 dark:bg-slate-900"
            />
            <select
              value={moneda}
              onChange={(e) => setMoneda(e.target.value as 'DOP' | 'USD')}
              className="min-h-[44px] rounded-lg border border-slate-200 px-2 text-sm dark:border-slate-700 dark:bg-slate-900"
            >
              <option value="DOP">RD$</option>
              <option value="USD">US$</option>
            </select>
          </div>
        ) : (
          <button
            onClick={() => setVerMonto(true)}
            className="mt-3 text-xs text-brand-600 dark:text-brand-300"
          >
            + Agregar un monto
          </button>
        )}

        <Button
          className="mt-4 w-full"
          disabled={!puede || crear.isPending}
          onClick={() => crear.mutate()}
        >
          <Plus className="h-4 w-4" /> {crear.isPending ? 'Anotando…' : 'Anotar'}
        </Button>
      </div>
    </>
  );
}
